import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  FiFileText, FiCheckCircle, FiAlertCircle, FiClock,
  FiRefreshCw, FiTrendingUp, FiZap,
} from 'react-icons/fi';
import { getDashboard, getApprovalSla, getValidationErrors } from '../../utils/journalApi';

/* ── Status colour palette ── */
const SC = {
  POSTED:            { bg: '#dcfce7', fg: '#15803d', bar: '#22c55e' },
  AWAITING_APPROVAL: { bg: '#fef3c7', fg: '#92400e', bar: '#f59e0b' },
  FAILED:            { bg: '#fee2e2', fg: '#b91c1c', bar: '#ef4444' },
  PENDING:           { bg: '#e0e7ff', fg: '#3730a3', bar: '#6366f1' },
  VALIDATING:        { bg: '#dbeafe', fg: '#1d4ed8', bar: '#3b82f6' },
  POSTING:           { bg: '#cffafe', fg: '#0e7490', bar: '#06b6d4' },
  CANCELLED:         { bg: '#f3f4f6', fg: '#374151', bar: '#9ca3af' },
  DRAFT:             { bg: '#f5f3ff', fg: '#6d28d9', bar: '#a78bfa' },
};

const TIER_COLORS = ['#6366f1','#f59e0b','#ef4444','#8b5cf6'];

/* ── Animated number ── */
const AnimatedCount = ({ target, suffix = '', decimals = 0 }) => {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 70, damping: 22, mass: 1.4 });
  const [display, setDisplay] = useState('0');
  const fired = useRef(false);
  useEffect(() => {
    const n = parseFloat(target);
    if (!fired.current && !isNaN(n)) { fired.current = true; mv.set(n); }
  }, [target, mv]);
  useEffect(() => spring.on('change', v => {
    setDisplay(decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString());
  }), [spring, decimals]);
  return <>{display}{suffix}</>;
};

/* ── Donut custom legend ── */
const DonutLegend = ({ data, total }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:6, justifyContent:'center' }}>
    {data.map(d => {
      const c = SC[d.name] || { bg:'#f3f4f6', fg:'#374151', bar:'#9ca3af' };
      const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
      return (
        <div key={d.name} style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:10, height:10, borderRadius:3, background:c.bar, flexShrink:0 }} />
          <span style={{ fontSize:11, color:'#57708d', flex:1, fontWeight:600 }}>{d.name.replace(/_/g,' ')}</span>
          <span style={{ fontSize:11, fontWeight:800, color:'#12213a', minWidth:24, textAlign:'right' }}>{d.value}</span>
          <span style={{ fontSize:10, color:'#a0b0c4', minWidth:34, textAlign:'right' }}>{pct}%</span>
        </div>
      );
    })}
  </div>
);

/* ── KPI card config ── */
const kpiCards = (dash) => [
  {
    label: 'Total Entries',
    value: dash?.total_entries,
    suffix: '',
    icon: <FiFileText size={20} />,
    grad: 'linear-gradient(135deg,#0f4c81 0%,#1976a0 100%)',
    shadow: '0 12px 28px rgba(15,76,129,0.32)',
    sub: 'All time',
  },
  {
    label: 'Posted',
    value: dash?.status_breakdown?.POSTED,
    suffix: '',
    icon: <FiCheckCircle size={20} />,
    grad: 'linear-gradient(135deg,#059669 0%,#10b981 100%)',
    shadow: '0 12px 28px rgba(5,150,105,0.30)',
    sub: 'Successfully posted',
  },
  {
    label: 'Success Rate',
    value: dash?.posting_success_rate,
    suffix: '%',
    decimals: 1,
    icon: <FiTrendingUp size={20} />,
    grad: 'linear-gradient(135deg,#0891b2 0%,#06b6d4 100%)',
    shadow: '0 12px 28px rgba(8,145,178,0.28)',
    sub: 'Of all submissions',
  },
  {
    label: 'Awaiting Approval',
    value: dash?.status_breakdown?.AWAITING_APPROVAL,
    suffix: '',
    icon: <FiClock size={20} />,
    grad: 'linear-gradient(135deg,#d97706 0%,#f59e0b 100%)',
    shadow: '0 12px 28px rgba(217,119,6,0.30)',
    sub: 'Pending review',
  },
  {
    label: 'Failed',
    value: dash?.status_breakdown?.FAILED,
    suffix: '',
    icon: <FiAlertCircle size={20} />,
    grad: 'linear-gradient(135deg,#dc2626 0%,#ef4444 100%)',
    shadow: '0 12px 28px rgba(220,38,38,0.28)',
    sub: 'Requires attention',
  },
];

const cardV = {
  hidden:  { opacity:0, y:24, scale:0.94 },
  visible: { opacity:1, y:0,  scale:1, transition:{ type:'spring', stiffness:200, damping:20 } },
};
const containerV = {
  hidden: {},
  visible: { transition:{ staggerChildren:0.07, delayChildren:0.04 } },
};

export default function JournalDashboard({ onNavigate }) {
  const [dash,    setDash]    = useState(null);
  const [sla,     setSla]     = useState([]);
  const [errors,  setErrors]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [cc,      setCc]      = useState('');

  const load = async (companyCode) => {
    setLoading(true);
    try {
      const [d, s, e] = await Promise.all([
        getDashboard(companyCode || undefined),
        getApprovalSla(),
        getValidationErrors(30, companyCode || undefined),
      ]);
      setDash(d.data);
      setSla(Array.isArray(s.data?.tiers) ? s.data.tiers : []);
      setErrors(Array.isArray(e.data?.errors) ? e.data.errors : []);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(''); }, []);

  const statusBreakdown = dash?.status_breakdown
    ? Object.entries(dash.status_breakdown).map(([name, value]) => ({ name, value })).filter(d => d.value > 0)
    : [];
  const totalEntries = statusBreakdown.reduce((s, d) => s + d.value, 0);
  const maxError = errors.length ? Math.max(...errors.map(e => e.count)) : 1;

  if (loading && !dash) return (
    <div className="journal-loading">
      <div className="journal-spinner" />
      <span>Loading dashboard…</span>
    </div>
  );

  return (
    <div>
      {/* ── Filter bar ── */}
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:18 }}>
        <input className="journal-input" placeholder="Filter by Company Code"
          maxLength={4} value={cc} onChange={e => setCc(e.target.value)} style={{ width:200 }} />
        <button className="journal-btn primary" onClick={() => load(cc)} disabled={loading}
          style={{ minWidth:105 }}>
          {loading
            ? <><div className="journal-spinner-sm" /> Loading…</>
            : <><FiRefreshCw size={13} /> Refresh</>}
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <motion.div className="jd-kpi-grid" variants={containerV} initial="hidden" animate="visible">
        {kpiCards(dash).map(c => (
          <motion.div key={c.label} className="jd-kpi-card" variants={cardV}
            style={{ background: c.grad, boxShadow: c.shadow }}
            whileHover={{ y:-4, boxShadow: c.shadow.replace('0.32','0.45').replace('0.30','0.42').replace('0.28','0.40') }}>
            <div className="jd-kpi-icon">{c.icon}</div>
            <div className="jd-kpi-body">
              <div className="jd-kpi-label">{c.label}</div>
              <div className="jd-kpi-value">
                {c.value != null
                  ? <AnimatedCount target={c.value} suffix={c.suffix} decimals={c.decimals || 0} />
                  : '—'}
              </div>
              <div className="jd-kpi-sub">{c.sub}</div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* ── Charts row ── */}
      <div className="journal-grid-2" style={{ marginBottom:14 }}>

        {/* Donut chart */}
        <div className="journal-panel">
          <div className="journal-panel-head">
            <h3>Entry Distribution</h3>
            {totalEntries > 0 && (
              <span style={{ fontSize:'0.78rem', color:'var(--j-muted)', fontWeight:700 }}>
                {totalEntries} total
              </span>
            )}
          </div>
          {statusBreakdown.length ? (
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ flex:'0 0 160px' }}>
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={statusBreakdown} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={46} outerRadius={72}
                      paddingAngle={2} strokeWidth={0}>
                      {statusBreakdown.map(e => (
                        <Cell key={e.name} fill={(SC[e.name]||{bar:'#c7c9e8'}).bar} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, n) => [v, n.replace(/_/g,' ')]}
                      contentStyle={{ fontSize:11, borderRadius:8, border:'1px solid #d8e3f0' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex:1 }}>
                <DonutLegend data={statusBreakdown} total={totalEntries} />
              </div>
            </div>
          ) : <div className="journal-empty">No data yet</div>}
        </div>

        {/* Status bar chart */}
        <div className="journal-panel">
          <div className="journal-panel-head"><h3>Status Breakdown</h3></div>
          {statusBreakdown.length ? (
            <ResponsiveContainer width="100%" height={192}>
              <BarChart data={statusBreakdown} margin={{ top:4, bottom:24, left:-14, right:4 }}
                barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(91,95,199,0.08)" vertical={false} />
                <XAxis dataKey="name"
                  tick={{ fontSize:9, fill:'#57708d', fontWeight:600 }}
                  tickFormatter={n => n.replace(/_/g,'\n').slice(0,8)}
                  interval={0} angle={-30} textAnchor="end" />
                <YAxis tick={{ fontSize:10, fill:'#57708d' }} />
                <Tooltip
                  formatter={(v, n) => [v, n.replace(/_/g,' ')]}
                  contentStyle={{ fontSize:11, borderRadius:8, border:'1px solid #d8e3f0' }} />
                <Bar dataKey="value" radius={[6,6,0,0]}>
                  {statusBreakdown.map(e => (
                    <Cell key={e.name} fill={(SC[e.name]||{bar:'#c7c9e8'}).bar} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="journal-empty">No data yet</div>}
        </div>
      </div>

      {/* ── SLA + Errors row ── */}
      <div className="journal-grid-2">

        {/* SLA */}
        <div className="journal-panel" style={{ marginBottom:14 }}>
          <div className="journal-panel-head">
            <h3>Approval SLA</h3>
            <span style={{ fontSize:'0.78rem', color:'var(--j-muted)' }}>Avg decision time</span>
          </div>
          {sla.length ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {sla.map((r, i) => {
                const color = TIER_COLORS[(r.tier - 1) % TIER_COLORS.length];
                const mins  = r.avg_decision_minutes ?? 0;
                const isGood = mins === 0 || mins < 60;
                return (
                  <div key={i} style={{
                    display:'flex', alignItems:'center', gap:12,
                    background: `${color}12`,
                    border: `1px solid ${color}30`,
                    borderRadius: 10, padding:'10px 14px',
                  }}>
                    <div style={{
                      width:36, height:36, borderRadius:10,
                      background: `${color}22`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      flexShrink:0,
                    }}>
                      <span style={{ fontSize:13, fontWeight:800, color }}>{r.tier}</span>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:12, fontWeight:700, color:'#12213a' }}>
                          Tier {r.tier}
                          <span style={{
                            marginLeft:8, fontSize:10, fontWeight:700, padding:'2px 7px',
                            borderRadius:20, background:`${color}20`, color,
                          }}>{r.status}</span>
                        </span>
                        <span style={{ fontSize:12, fontWeight:800, color }}>
                          {mins > 0 ? `${mins} min` : '—'}
                        </span>
                      </div>
                      <div style={{ marginTop:6, height:5, background:`${color}20`, borderRadius:4 }}>
                        <div style={{
                          height:'100%', borderRadius:4,
                          background: color,
                          width: `${Math.min(100, mins > 0 ? Math.log1p(mins) / Math.log1p(500) * 100 : 0)}%`,
                          transition:'width 1s ease',
                        }} />
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', marginTop:3 }}>
                        <span style={{ fontSize:10, color:'#a0b0c4' }}>{r.count} decisions</span>
                        <span style={{ fontSize:10, fontWeight:700, color: isGood ? '#15803d' : '#d97706' }}>
                          {isGood ? '✓ On track' : '⚠ Delayed'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <div className="journal-empty">No SLA data</div>}
        </div>

        {/* Validation Errors */}
        <div className="journal-panel" style={{ marginBottom:14 }}>
          <div className="journal-panel-head">
            <h3>Top Validation Errors</h3>
            <span style={{ fontSize:'0.78rem', color:'var(--j-muted)' }}>Last 30 days</span>
          </div>
          {errors.length ? (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {errors.map((e, i) => {
                const isErr  = e.severity === 'ERROR';
                const color  = isErr ? '#ef4444' : '#f59e0b';
                const bgCol  = isErr ? '#fee2e2' : '#fef3c7';
                const pct    = Math.max(6, (e.count / maxError) * 100);
                return (
                  <div key={i} style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <span style={{
                          fontSize:10, fontWeight:700, padding:'2px 7px',
                          borderRadius:20, background:bgCol, color,
                        }}>{e.severity}</span>
                        <code style={{ fontSize:11, fontFamily:'monospace', color:'#12213a', fontWeight:700 }}>
                          {e.type}
                        </code>
                      </div>
                      <span style={{ fontSize:13, fontWeight:800, color }}>{e.count}</span>
                    </div>
                    <div style={{ height:5, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                      <motion.div
                        initial={{ width:0 }}
                        animate={{ width:`${pct}%` }}
                        transition={{ duration:0.8, delay: i * 0.1 }}
                        style={{ height:'100%', background:color, borderRadius:4 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="journal-empty">
              <FiZap size={28} style={{ color:'#22c55e', marginBottom:6 }} />
              No validation errors
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
